import { SignedDistanceField } from '../../../core/models/SignedDistanceField';
import { CustomObject } from './CustomObject';
import { ImageObject } from '../../image/ImageObject';
import { Grid } from '../Grid';
import { TextureView } from '~/engine/view/TextureView';
import { RenderStorage } from '~/engine/core/render/RenderStorage';
import {
    ContourRenderer,
    ContourRenderOptions,
} from '~/engine/core/render/renderers/ContourRenderer';
import { ContourTexture } from '../../structure/ContourTexture';
import { ReadonlyVec3 } from 'gl-matrix';

/**
 * Abstract class for objects that are used as targets by a Tool
 */
export class CustomTargetObject extends CustomObject {
    protected _grid: Grid;

    protected readonly _targetImage: ImageObject;

    protected readonly _imageGrid: Grid;

    /**
     * {@link SignedDistanceField} that stores the {@link WebGLTexture} with signed distance values.
     */
    protected sdf?: SignedDistanceField;

    protected _contourTexture?: ContourTexture;

    protected _color: ReadonlyVec3 = [1.0, 0.0, 0.0];

    /** Contour line thickness forwarded to ContourRenderer. */
    protected _contourThickness = 1.5;

    public constructor(
        image: ImageObject,
        texture?: ContourTexture,
        color?: ReadonlyVec3
    ) {
        super();
        this._targetImage = image;
        this._imageGrid = image.parent.grid;
        this._grid = new Grid(
            0,
            0,
            0,
            this._imageGrid.resX,
            this._imageGrid.resY,
            this._imageGrid.resZ,
            null
        );
        this._contourTexture = texture;
        if (color) {
            this._color = color;
        }
    }

    /** Rendering options forwarded to ContourRenderer. */
    public renderOptions?: ContourRenderOptions;

    public render(view: TextureView): void {
        if (!this._contourTexture) {
            return;
        }
        const contourRenderer = RenderStorage.getRenderer(ContourRenderer);
        contourRenderer.renderContourTexture(
            view,
            this._contourTexture,
            this._color,
            this._contourThickness,
            this.renderOptions
        );
    }

    /**
     * Method to retrieve the {@link WebGLTexture} stored within the {@link sdf} of this {@link CustomTargetObject}.\
     * This method will return undefined if the {@link CustomTargetObject} has not been {@link update updated} yet.
     * @returns Reference to texture.
     */
    public get texture(): WebGLTexture | undefined {
        return (
            this._contourTexture?.signedDistanceField.texture ??
            this.sdf?.texture
        );
    }

    public get contourTexture(): ContourTexture | undefined {
        return this._contourTexture;
    }

    public set contourTexture(value: ContourTexture | undefined) {
        this._contourTexture = value;
    }

    public get grid(): Grid {
        return this._grid;
    }

    /**
     * Method to delete this {@link CustomTargetObject}.\
     * This will {@link SignedDistanceField.delete delete} the {@link sdf}.\
     * It is important to call this method when the {@link CustomTargetObject} is no longer needed.\
     * This method may be overridden by subclasses to delete additional resources. However the super method should always be called.
     * @returns True to indicate that the {@link CustomTargetObject} was successfully deleted.
     */
    public delete() {
        if (this.sdf) {
            this.sdf.delete();
        }
        if (this._contourTexture) {
            this._contourTexture.delete();
        }
    }
}
